import { FC, memo, useMemo } from "react";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import { useCompanyFilterSlice } from "../../hooks";
import { TopicsSection } from "./sections/TopicsSection";
import { SignalTimingSection } from "./sections/SignalTimingSection";
import { BuyerContextSection } from "./sections/BuyerContextSection";
import { LocationSection } from "./sections/LocationSection";

/**
 * IntentFilter component for the Segments feature.
 * Allows filtering companies by buyer intent signals.
 */
export const IntentFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("intent");

  const hasLocationFilters = useMemo(() => {
    return (
      slice.location.countryAlpha2s.length > 0 ||
      slice.location.excludedCountryAlpha2s.length > 0 ||
      slice.location.regionNames.length > 0 ||
      slice.location.stateIds.length > 0
    );
  }, [slice.location]);

  const shouldShowWarning = useMemo(() => {
    if (slice.topicIds.length > 0) return false;
    return (
      slice.signalScore !== "HIGH" ||
      slice.buyersJourneyIds.length > 0 ||
      slice.contextTypeIds.length > 0 ||
      slice.numberOfCadences !== 4 ||
      hasLocationFilters
    );
  }, [
    hasLocationFilters,
    slice.buyersJourneyIds.length,
    slice.contextTypeIds.length,
    slice.numberOfCadences,
    slice.signalScore,
    slice.topicIds.length,
  ]);

  return (
    <Box id="intent-filter-container" sx={{ mx: -2, mt: -2 }}>
      {shouldShowWarning && (
        <Box>
          <Alert severity="warning" sx={{ py: 0.5 }}>
            <AlertTitle sx={{ mb: 0 }}>Topics Required</AlertTitle>
            Select at least one topic to apply Intent filters.
          </Alert>
        </Box>
      )}

      <TopicsSection />
      <SignalTimingSection />
      <BuyerContextSection />
      <LocationSection />
    </Box>
  );
});

IntentFilter.displayName = "IntentFilter";
